import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Sun, MapPin, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { staffAPI } from '../../services/api';
import LoadingSpinner from '../UI/LoadingSpinner';
import { StatusBadge } from '../UI/StatusBadge';
import { getTodayInSingaporeTimezone } from '../../utils/dateUtils';

const RestDayWorkAssignmentList = ({ currentUser }) => {
  const { data: assignments = [], isLoading, error } = useQuery({
    queryKey: ['restday-work-my-assignments'],
    queryFn: staffAPI.getRestDayWorkMyAssignments,
    select: (r) => r.data,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 flex justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Unable to Load</h3>
        <p className="text-sm text-gray-500">Failed to load your rest day work assignments. Please try again.</p>
      </div>
    );
  }

  if (assignments.length === 0) {
    return (
      <div className="text-center py-12">
        <Sun className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Rest Day Work Assignments</h3>
        <p className="text-sm text-gray-500">You don't have any active rest day work assignments.</p>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Asia/Singapore' });
  };

  const formatTime = (t) => {
    if (!t) return '—';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
  };

  const today = getTodayInSingaporeTimezone();

  return (
    <div className="space-y-4">
      {assignments.map((assignment) => {
        const isToday = assignment.is_current;
        const hasUpcoming = assignment.upcoming_dates.length > 0;
        const isPast = !hasUpcoming && assignment.past_dates.length > 0;

        return (
          <div
            key={assignment.id}
            className={`bg-white rounded-2xl shadow-sm border overflow-hidden ${
              isToday ? 'ring-2 ring-blue-400' : ''
            }`}
          >
            {/* Header */}
            <div className={`px-5 py-4 border-b ${isToday ? 'bg-blue-50' : 'bg-gray-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100">
                    <Sun className="w-5 h-5 text-blue-800" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900">{assignment.request_number}</span>
                      {isToday && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Today
                        </span>
                      )}
                      {isPast && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          Completed
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">Requested by {assignment.requested_by}</p>
                  </div>
                </div>
                <StatusBadge status={assignment.status} />
              </div>
            </div>

            {/* Body */}
            <div className="px-5 py-4 space-y-3">
              {/* Project */}
              <div className="flex items-center gap-2 text-sm">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span className="font-medium text-gray-900">{assignment.project_code}</span>
                {assignment.project_name && (
                  <span className="text-gray-500">— {assignment.project_name}</span>
                )}
              </div>

              {/* Schedule Time */}
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <Clock className="w-4 h-4 text-gray-400" />
                <span>{formatTime(assignment.start_time)} – {formatTime(assignment.end_time)}</span>
                {assignment.break_minutes > 0 && (
                  <span className="text-gray-400">({assignment.break_minutes} min break)</span>
                )}
              </div>

              {/* Role Badge */}
              <div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  assignment.role === 'safety' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  {assignment.role === 'safety' ? 'Safety Personnel' : 'Worker'}
                </span>
              </div>

              {/* Upcoming Dates */}
              {hasUpcoming && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">
                    {isToday ? 'Today & Upcoming' : 'Upcoming'} ({assignment.upcoming_dates.length} {assignment.upcoming_dates.length === 1 ? 'day' : 'days'})
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {assignment.upcoming_dates.map((d) => (
                      <span
                        key={d}
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          d === today
                            ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {formatDate(d)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Past summary */}
              {assignment.past_dates.length > 0 && hasUpcoming && (
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  {assignment.past_dates.length} {assignment.past_dates.length === 1 ? 'day' : 'days'} completed
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default RestDayWorkAssignmentList;
